import store from "@/stores/store";
import axios from "axios";
import utils from "@/stores/utils";
import orders from "./submodules/customerorders";
import customerstats from "./submodules/customerstats";
import documents from "./submodules/documents";
import catalogentries from "./submodules/catalogentries";
import catalogs from "./submodules/catalogs";
import invoices from "./submodules/invoices";
import { catalogCustomerIdForOrder, canReadCatalogCustomerForOrder } from "@/utils/customer-order-catalog";
import { CSV_EXPORT_LIMIT } from "@/constants/csv-export";

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

/** In-flight request deduplication (concurrent identical fetches share one promise). */
let inflightFetchShipments = null;
let inflightFetchOrderItems = null;
/** @type {number|string|null} */
let inflightFetchOrderItemsOrderId = null;
let inflightFetchOrder = null;
/** @type {number|string|null} */
let inflightFetchOrderOrderId = null;
let inflightFetchCustomerStats = null;
/**
 * Buyer the customer stats in store (or in flight) belong to: remounting the
 * catalog entries block must not refetch them.
 * @type {number|string|null}
 */
let customerStatsBuyerId = null;

function resetInflightFetchOrderItems() {
    inflightFetchOrderItems = null;
    inflightFetchOrderItemsOrderId = null;
}

function resetInflightFetchOrder() {
    inflightFetchOrder = null;
    inflightFetchOrderOrderId = null;
}

function resetCustomerStatsFetch() {
    inflightFetchCustomerStats = null;
    customerStatsBuyerId = null;
}

function sameOrderId(left, right) {
    if (left == null || left === "" || right == null || right === "") {
        return false;
    }
    return String(left) === String(right);
}

async function requestCustomerStats(commit, buyerId) {
    try {
        const response = await axios.get(`/api/customers/${buyerId}/stats/`, {
            params: { profile: "customer" },
        });
        commit("updateCustomerStats", response.data);
        return response;
    } catch (xhr_error) {
        const error = utils.handleError(xhr_error);
        resetCustomerStatsFetch();
        commit("updateCustomerStatsLoadingErrors", error.details);
        throw error;
    } finally {
        inflightFetchCustomerStats = null;
    }
}

function shouldFetchSection(section, targetSection) {
    return !section || section === targetSection;
}

function canFetchOrderBillingAccount(state, permissions) {
    if (!state.order?.buyer_data?.billingaccount) {
        return false;
    }
    if (
        state.order.buyer_data.owner !==
        store.getters["session/current_entity_id"]
    ) {
        return false;
    }
    if (permissions.indexOf("billing.view_billingaccount") === -1) {
        return false;
    }
    if (!state.order.ready) {
        return false;
    }
    return (
        permissions.indexOf("billing.view_invoice") !== -1 ||
        permissions.indexOf("billing.add_invoice") !== -1 ||
        permissions.indexOf("order.bill_customerorder") !== -1
    );
}

const state = {
    order: null,
    order_loaded: false,
    order_loading: false,
    order_loading_errors: null,
    order_saving: false,
    order_saving_errors: null,

    order_setting_readyness: false,
    updating_order_status: false,
    order_closing_register_sale: false,
    assigning_order: false,

    deleting_order: false,
    deleting_order_errors: null,

    orderitem: false,
    orderitem_loading: false,
    orderitem_loading_errors: null,
    orderitem_saving: false,
    orderitem_saving_errors: null,
    orderitem_deleting: false,
    orderitem_deleting_errors: null,

    orderitems: null,
    orderitems_count: 0,
    orderitems_filters: { limit: 10000, ordering: "id" },
    orderitems_loaded: false,
    orderitems_loading: false,
    orderitems_loading_errors: null,
    orderitems_cancel_source: null,

    customers: [],
    customers_count: 0,
    customers_loading: false,
    customers_loading_errors: null,

    deliverysites: [],
    deliverysites_count: 0,
    deliverysites_loading: false,
    deliverysites_loading_errors: null,

    stocks: [],
    stocks_count: 0,
    stocks_loading: false,
    stocks_loading_errors: null,

    events: [],
    events_loading: false,
    events_loaded: false,
    events_loading_errors: null,
    events_count: 0,

    shipments: [],
    shipments_filters: { limit: 10000, ordering: "-delivery_date" },
    shipments_count: 0,
    shipments_loaded: false,
    shipments_loading: false,
    shipments_loading_errors: null,

    order_delegating: false,
    order_billing: false,
    creating_suborders: false,

    billingaccount: null,
    customer_stats: {},
    customer_stats_loading: false,
    customer_stats_loading_errors: null,

    completing_order: false,
    cancelling_order: false,
    opening_shipment_creation: false,

    sending_order_form: false,
    marking_customerorder_form_communicated: false,

    // Section ("billing", ...) to scroll to after the next order view mount.
    // Section switches remount the whole view, so the request must survive it.
    scroll_to_section: null,
};

const mutations = {
    updateOrder(state, order) {
        state.order_loaded = true;

        if (order && state.order && sameOrderId(state.order.id, order.id)) {
            state.order = order;
            return;
        }

        resetInflightFetchOrder();
        resetCustomerStatsFetch();
        state.order = order;

        state.orderitems = [];
        state.orderitems_count = 0;
        state.orderitems_loaded = false;
        state.orderitems_loading = false;
        state.orderitems_loading_errors = null;
        state.orderitem = null;
        state.orderitem_loading = false;
        state.orderitem_loading_errors = null;
        state.orderitem_saving = false;
        state.orderitem_saving_errors = null;
        state.orderitem_deleting = false;
        state.orderitem_deleting_errors = null;
        state.order_loaded = true;
        state.order_loading = false;
        state.order_loading_errors = null;
        state.customer_stats = {};
        state.customer_stats_loading = false;
        state.customer_stats_loading_errors = null;
        state.order_setting_readyness = false;
        state.updating_order_status = false;
        state.assigning_order = false;
        state.deleting_order = false;
        state.deleting_order_errors = null;
        state.order_delegating = false;
        state.order_billing = false;
        state.creating_suborders = false;
        state.billingaccount = null;
        state.sending_order_form = false;
        state.marking_customerorder_form_communicated = false;
        state.scroll_to_section = null;
        state.customers = [];
        state.customers_count = 0;
        state.customers_loading = false;
        state.customers_loading_errors = null;

        state.deliverysites = [];
        state.deliverysites_count = 0;

        state.events = [];
        state.events_count = 0;
        state.events_loaded = false;
        state.events_loading_errors = null;
        
        state.shipments = [];
        state.shipments_count = 0;
        state.shipments_loaded = false;
        state.shipments_loading = false;
        state.shipments_loading_errors = null;
        
        state.orderitems_filters = {};
        state.shipments_filters = {};

        state.orderitems_filters = loadFiltersFromStorage(
            getStorageKey("customerorder", "items"),
            { limit: 10000, ordering: "id" },
            ["limit"]
        );
        state.shipments_filters = loadFiltersFromStorage(
            getStorageKey("customerorder", "shipments"),
            { limit: 10000, ordering: "-delivery_date" },
            ["limit"]
        );
        
        if (state.orderitems_cancel_source) {
            state.orderitems_cancel_source.cancel("canceled");
            state.orderitems_cancel_source = null;
        }

        resetInflightFetchOrderItems();
    },
    updateOrderCompleting(state, completing) {
        state.completing_order = completing;
    },
    updateOrderOpeningShipmentCreation(state, opening) {
        state.opening_shipment_creation = opening;
    },
    updateOrderClosingRegisterSale(state, closing) {
        state.order_closing_register_sale = closing;
    },
    updateOrderCancelling(state, cancelling) {
        state.cancelling_order = cancelling;
    },
    updateOrderLoading(state, loading) {
        state.order_loading = loading;
    },
    updateOrderLoadingErrors(state, errors) {
        state.order_loaded = true;
        state.order_loading_errors = errors;
    },
    updateOrderSaving(state, saving) {
        state.order_saving = saving;
    },
    updateOrderSavingErrors(state, errors) {
        state.order_saving_errors = errors;
    },

    updateSettingOrderReadyness(state, setting) {
        state.order_setting_readyness = setting;
    },
    updateUpdatingOrderStatus(state, status) {
        state.updating_order_status = status;
    },
    updateOrderDeleting(state, deleting) {
        state.deleting_order = deleting;
    },
    updateOrderDeletingErrors(state, errors) {
        state.deleting_order_errors = errors;
    },
    updateOrderItem(state, orderitem) {
        state.orderitem = orderitem;
    },
    updateOrderItemLoading(state, loading) {
        state.orderitem_loading = loading;
    },
    updateOrderItemLoadingErrors(state, errors) {
        state.orderitem_loading_errors = errors;
    },
    updateOrderItemSaving(state, saving) {
        state.orderitem_saving = saving;
    },
    updateOrderItemSavingErrors(state, errors) {
        state.orderitem_saving_errors = errors;
    },
    updateOrderItemDeleting(state, deleting) {
        state.orderitem_deleting = deleting;
    },
    updateOrderItemDeletingErrors(state, errors) {
        state.orderitem_deleting_errors = errors;
    },
    updateOrderItems(state, orderitems) {
        state.orderitems = orderitems;
    },
    updateOrderItemsCount(state, count) {
        state.orderitems_count = count;
    },
    updateOrderItemsFilters(state, orderitems_filters) {
        state.orderitems_filters = {
            ...orderitems_filters,

        };
        
        // Save to localStorage
        const storageKey = getStorageKey('customerorder', 'items');
        saveFiltersToStorage(storageKey, state.orderitems_filters, ["ordering"]);
    },
    updateOrderItemsLoading(state, loading) {
        state.orderitems_loading = loading;
    },
    updateOrderItemsLoaded(state, loaded) {
        state.orderitems_loaded = loaded;
    },
    updateOrderItemsLoadingErrors(state, errors) {
        state.orderitems_loading_errors = errors;
    },
    updateOrderItemsCancelSource(state, orderitems_cancel_source) {
        state.orderitems_cancel_source = orderitems_cancel_source;
    },
    updateCustomers(state, customers) {
        state.customers = customers;
    },
    updateCustomersCount(state, count) {
        state.customers_count = count;
    },
    updateCustomersLoading(state, loading) {
        state.customers_loading = loading;
    },
    updateCustomersLoadingErrors(state, errors) {
        state.customers_loading_errors = errors;
    },
    updateDeliverySites(state, deliverysites) {
        state.deliverysites = deliverysites;
    },
    updateDeliverySitesCount(state, count) {
        state.deliverysites_count = count;
    },
    updateDeliverySitesLoading(state, loading) {
        state.deliverysites_loading = loading;
    },
    updateDeliverySitesLoadingErrors(state, errors) {
        state.deliverysites_loading_errors = errors;
    },
    updateStocks(state, stocks) {
        state.stocks = stocks;
    },
    updateStocksLoading(state, loading) {
        state.stocks_loading = loading;
    },
    updateStocksLoadingErrors(state, errors) {
        state.stocks_loading_errors = errors;
    },
    updateStocksCount(state, count) {
        state.stocks_count = count;
    },
    updateAssigningOrder(state, assigning_order) {
        state.assigning_order = assigning_order;
    },
    updateEvents(state, events) {
        state.events = events;
    },
    updateEventsLoading(state, events_loading) {
        state.events_loading = events_loading;
    },
    updateEventsLoadingErrors(state, events_loading_errors) {
        state.events_loading_errors = events_loading_errors;
    },
    updateEventsCount(state, events_count) {
        state.events_count = events_count;
    },
    updateShipments(state, shipments) {
        state.shipments = shipments;
    },
    updateShipmentsLoading(state, shipments_loading) {
        state.shipments_loading = shipments_loading;
        state.shipments_loaded = true;
    },
    updateShipmentsLoadingErrors(state, shipments_loading_errors) {
        state.shipments_loading_errors = shipments_loading_errors;
    },
    updateShipmentsCount(state, shipments_count) {
        state.shipments_count = shipments_count;
    },
    updateShipmentsFilters(state, shipments_filters) {
        state.shipments_filters = shipments_filters;
        // Save to localStorage
        const storageKey = getStorageKey("customerorder", "shipments");
        saveFiltersToStorage(storageKey, state.shipments_filters, ["ordering"]);       
    },
    updateCreatingSuborders(state, creating_suborders) {
        state.creating_suborders = creating_suborders;
    },
    updateScrollToSection(state, scroll_to_section) {
        state.scroll_to_section = scroll_to_section;
    },
    updateOrderBilling(state, order_billing) {
        state.order_billing = order_billing;
    },
    updateBillingAccount(state, billingaccount) {
        state.billingaccount = billingaccount;
    },
    updateSendingOrderForm(state, sending_order_form) {
        state.sending_order_form = sending_order_form;
    },
    updateMarkingCustomerOrderFormCommunicated(state, marking) {
        state.marking_customerorder_form_communicated = marking;
    },

    updateCustomerStats(state, customer_stats) {
        state.customer_stats = customer_stats;
        state.customer_stats_loading = false;
        state.customer_stats_loading_errors = null;
    },
    updateCustomerStatsLoading(state, loading) {
        state.customer_stats_loading = loading;
        state.customer_stats_loading_errors = null;
    },
    updateCustomerStatsLoadingErrors(state, stats_loading_errors) {
        state.customer_stats = {};
        state.customer_stats_loading = false;
        state.customer_stats_loading_errors = stats_loading_errors;
    },
};

const actions = {

    async fetchEvents({ commit, dispatch, state }, params) {
        commit("updateEventsLoading", true);

        try {
            const response = await axios.get("/api/events/", {
                params: {
                    object_type: "order",
                    object_id: state.order.order_id,
                    ordering: "-event_date"
                }
            });
            const events = [...response.data.results];
            let next = response.data.next;
            while (next) {
                const page = await axios.get(next);
                events.push(...page.data.results);
                next = page.data.next;
            }
            commit("updateEvents", events);
            commit("updateEventsCount", response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateEventsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateEventsLoading", false);
        }
    },

    async fetchShipments({ commit, dispatch, state }, params) {

        if (
            store.getters["session/current_user_permissions"].indexOf(
                "core.view_shipment"
            ) == -1
        ) {
            return;
        }

        if (inflightFetchShipments) {
            return inflightFetchShipments;
        }

        inflightFetchShipments = (async () => {
            commit("updateShipmentsLoading", true);

            try {
                let filters = {
                    ordering: "-delivery_date",
                    ...(state.shipments_filters || {}),
                    limit: 10000,
                    offset: 0,
                };
                const response = await axios.get(`/api/customerorders/${state.order.id}/shipments/`, {
                    params: filters
                });
                commit("updateShipments", response.data.results);
                commit("updateShipmentsCount", response.data.count);
                return response;
            } catch (xhr_error) {
                const error = utils.handleError(xhr_error);
                commit("updateShipmentsLoadingErrors", error.details);
                throw error;
            } finally {
                commit("updateShipmentsLoading", false);
                inflightFetchShipments = null;
            }
        })();

        return inflightFetchShipments;
    },

    async fetchOrder({ commit, dispatch, state }, params) {
        if (!state.order?.id) {
            return;
        }

        const orderId = state.order.id;
        const force = Boolean(params && params.force);

        if (inflightFetchOrder && inflightFetchOrderOrderId === orderId) {
            if (!force) {
                return inflightFetchOrder;
            }
            try {
                await inflightFetchOrder;
            } catch {
                /* stale in-flight fetch; start a new one below */
            }
        }

        if (inflightFetchOrder) {
            resetInflightFetchOrder();
        }

        inflightFetchOrderOrderId = orderId;
        inflightFetchOrder = (async () => {
            commit("updateOrderLoading", true);
            commit("updateOrderLoadingErrors", null);

            const url = `/api/customerorders/${orderId}/`;

            try {
                const response = await axios.get(url);
                if (!sameOrderId(state.order?.id, orderId)) {
                    return;
                }
                commit("updateOrderLoading", false);
                commit("updateOrder", response.data);
                return response.data;
            } catch (xhr_error) {
                if (!sameOrderId(state.order?.id, orderId)) {
                    return;
                }
                const error = utils.handleError(xhr_error);
                commit("updateOrderLoading", false);
                commit("updateOrderLoadingErrors", error.details);
                throw error;
            } finally {
                if (inflightFetchOrderOrderId === orderId) {
                    resetInflightFetchOrder();
                }
            }
        })();

        return inflightFetchOrder;
    },

    async csvExport({ commit, dispatch, state }, params) {

        // Without an explicit limit the API falls back to its default page of 20 rows,
        // silently truncating any larger order.
        const url = `/api/customerorderitems/?order=${state.order.id}&format=csv&limit=${CSV_EXPORT_LIMIT}`;
        window.open(url, "_blank");
    },

    async setOrderReadyness({ commit, dispatch, state }, params) {

        const url = `/api/customerorders/${state.order.id}/ready/`;
        commit("updateSettingOrderReadyness", true);
        try {
            const payload = { ready: params.ready };
            if (params.productstrategy != null) {
                payload.productstrategy = params.productstrategy;
            }
            if (params.buyer_confirmation_waiver != null) {
                payload.buyer_confirmation_waiver = params.buyer_confirmation_waiver;
            }
            await axios.put(url, payload);
            const refreshed = await dispatch("fetchOrder");
            const parallel = [
                dispatch("fetchOrderItems"),
                dispatch("fetchEvents"),
                dispatch("session/fetchStats", null, { root: true }),
            ];
            if (refreshed?.suborders_needed || refreshed?.suborders_count) {
                parallel.push(dispatch("suborders/fetchOrders"));
            }
            await Promise.all(parallel);
            return { data: refreshed };
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateSettingOrderReadyness", false);
        }
    },

    async fetchDepartureWarehouseOptions({ state }, { scopeOrderId } = {}) {
        const detailPk =
            scopeOrderId != null &&
            scopeOrderId !== undefined &&
            scopeOrderId !== ""
                ? scopeOrderId
                : state.order.id;
        const response = await axios.get(
            `/api/customerorders/${detailPk}/departure-warehouse-options/`
        );
        return response.data;
    },

    async patchDepartureWarehouse(
        { dispatch, state },
        {
            scopeOrderId,
            fulfillmentMode,
            fulfillmentDepartureStock,
            refreshOrderItems = true,
        }
    ) {
        const orderPk =
            scopeOrderId != null &&
            scopeOrderId !== undefined &&
            scopeOrderId !== ""
                ? scopeOrderId
                : state.order.id;
        await axios.patch(`/api/customerorders/${orderPk}/fulfillment/`, {
            mode: fulfillmentMode,
            stock:
                fulfillmentDepartureStock !== undefined &&
                fulfillmentDepartureStock !== null
                    ? fulfillmentDepartureStock
                    : null,
        });
        const refreshed = await dispatch("fetchOrder");
        const parallel = [];
        if (refreshOrderItems) {
            parallel.push(dispatch("fetchOrderItems"));
        }
        if (refreshed?.suborders_count) {
            parallel.push(dispatch("suborders/fetchOrders"));
        }
        if (parallel.length) {
            await Promise.all(parallel);
        }
        return refreshed;
    },

    async cancelOrder({ commit, dispatch, state }, params) {
        const url = `/api/customerorders/${state.order.id}/cancel/`;
        commit("updateOrderCancelling", true);
        try {
            const response = await axios.post(url);
            dispatch("fetchOrder");
            dispatch("fetchEvents");
            dispatch("fetchOrderItems");
            dispatch("fetchShipments");
            dispatch("invoices/fetchInvoices");
            if (state.order.suborders_count) {
                dispatch("suborders/fetchOrders");
            }
            dispatch("session/fetchStats", null, { root: true });
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateOrderCancelling", false);
        }
    },

    async markOrderDelivered({ commit, dispatch, state }, params = {}) {
        const orderId = params?.orderId ?? state.order.id;
        const url = `/api/customerorders/${orderId}/mark-delivered/`;
        commit("updateOrderCompleting", true);
        commit("updateUpdatingOrderStatus", true);
        try {
            const response = await axios.post(url);
            if (orderId === state.order?.id) {
                dispatch("fetchOrder");
                dispatch("fetchEvents");
                dispatch("fetchOrderItems");
                dispatch("fetchShipments");
                dispatch("invoices/fetchInvoices");
                if (state.order.suborders_count) {
                    dispatch("suborders/fetchOrders");
                }
                dispatch("session/fetchStats", null, { root: true });
            }
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateOrderCompleting", false);
            commit("updateUpdatingOrderStatus", false);
        }
    },

    async completeOrder({ dispatch }, params = {}) {
        return dispatch("markOrderDelivered", params);
    },

    async closeRegisterSale({ commit, dispatch, state }) {
        const url = `/api/customerorders/${state.order.id}/close-register-sale/`;
        commit("updateOrderClosingRegisterSale", true);
        commit("updateUpdatingOrderStatus", true);
        try {
            await axios.post(url);
            dispatch("fetchOrder");
            dispatch("fetchEvents");
            dispatch("fetchOrderItems");
            dispatch("session/fetchStats", null, { root: true });
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateOrderClosingRegisterSale", false);
            commit("updateUpdatingOrderStatus", false);
        }
    },

    async markAsPaid({ commit, dispatch, state }, params) {

        commit("updateOrderSaving", true);
        const url = `/api/customerorders/${state.order.id}/paid/`;

        try {
            const response = await axios.put(url, { paid: params.paid });
            dispatch("fetchOrder");
            dispatch("fetchEvents");
            dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        } finally {
            commit("updateOrderSaving", false);
        }
    },

    async deleteOrder({ commit, dispatch, state }, params) {

        const url = `/api/customerorders/${params.instance.id}/`;

        commit("updateOrderDeleting", true);
        commit("updateOrderDeletingErrors", null);

        try {
            const response = await axios.delete(url);
            commit("updateOrderDeleting", false);
            await dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateOrderDeleting", false);
            commit("updateOrderDeletingErrors", error.details);
            throw error;
        }
    },

    async deleteOrderItem({ commit, dispatch, state }, params) {
        commit("updateOrderItemDeleting", true);
        commit("updateOrderItemDeletingErrors", null);

        const url = `/api/customerorderitems/${params.instance.id}/`;

        try {
            const response = await axios.delete(url);
            commit("updateOrderItemDeleting", false);
            await dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateOrderItemDeleting", false);
            commit("updateOrderItemDeletingErrors", error.details);
            throw error;
        }
    },

    async saveOrder({ commit, dispatch, state }, params) {

        commit("updateOrderSaving", true);
        commit("updateOrderSavingErrors", null);

        let url = "/api/customerorders/";
        let method = axios.post;

        if (params.instance.id) {
            url = `/api/customerorders/${params.instance.id}/`;
            method = axios.put;
        }

        try {
            const response = await method(url, params.instance);
            commit("updateOrderSaving", false);
            if (params.instance.id) {
                await dispatch("fetchOrder");
            }
            await dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateOrderSaving", false);
            commit("updateOrderSavingErrors", error.details);
            throw error;
        }
    },

    async fetchOrderItems({ commit, state }) {
        const order = state.order;
        if (!order?.id) {
            return;
        }

        const orderId = order.id;

        if (
            inflightFetchOrderItems &&
            inflightFetchOrderItemsOrderId === orderId
        ) {
            return inflightFetchOrderItems;
        }

        if (inflightFetchOrderItems) {
            resetInflightFetchOrderItems();
        }

        if (state.orderitems_cancel_source) {
            state.orderitems_cancel_source.cancel("canceled");
        }

        inflightFetchOrderItemsOrderId = orderId;
        inflightFetchOrderItems = (async () => {
            commit("updateOrderItemsLoading", true);
            commit("updateOrderItemsLoadingErrors", null);

            const cancelSource = axios.CancelToken.source();
            commit("updateOrderItemsCancelSource", cancelSource);

            try {
                const response = await axios.get("/api/customerorderitems/", {
                    params: {
                        order: orderId,
                        stock: order.stock,
                        ...state.orderitems_filters,
                    },
                    cancelToken: cancelSource.token,
                });

                if (!sameOrderId(state.order?.id, orderId)) {
                    return;
                }

                commit("updateOrderItems", response.data.results);
                commit("updateOrderItemsCount", response.data.count);
                commit("updateOrderItemsCancelSource", null);
                commit("updateOrderItemsLoaded", true);
                return response;
            } catch (xhr_error) {
                if (axios.isCancel(xhr_error)) {
                    return;
                }
                if (!sameOrderId(state.order?.id, orderId)) {
                    return;
                }
                const error = utils.handleError(xhr_error);
                commit("updateOrderItemsLoadingErrors", error.details);
                throw error;
            } finally {
                if (inflightFetchOrderItemsOrderId === orderId) {
                    resetInflightFetchOrderItems();
                }
                if (sameOrderId(state.order?.id, orderId)) {
                    commit("updateOrderItemsLoading", false);
                }
            }
        })();

        return inflightFetchOrderItems;
    },

    async fetchCustomers({ commit, dispatch, state }, params) {

        commit("updateCustomersLoading", true);
        commit("updateCustomersLoadingErrors", null);

        try {
            const response = await axios.get("/api/entities/", {
                params: { limit: 1000, ordering: "name", profile: "customer" },
            });
            commit("updateCustomers", response.data.results);
            commit("updateCustomersCount", response.data.count);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updateCustomersLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateCustomersLoading", false);
        }
    },

    async fetchDeliverySites({ commit }, { customer_id }) {
        if (
            store.getters["session/current_user_permissions"].indexOf(
                "core.view_customer"
            ) == -1
        ) {
            return;
        }

        commit("updateDeliverySitesLoading", true);
        commit("updateDeliverySitesLoadingErrors", null);

        try {

            const response = await axios.get(`/api/customers/${customer_id}/deliverysites/`, {
                params: { limit: 1000, ordering: "name", customer: customer_id },
            });
            commit("updateDeliverySites", response.data.results);
            commit("updateDeliverySitesCount", response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateDeliverySitesLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateDeliverySitesLoading", false);
        }
    },

    async fetchStocks({ commit, dispatch, state }, params) {

        commit("updateStocksLoading", true);
        commit("updateStocksLoadingErrors", null);

        try {
            const response = await axios.get("/api/stocks/", {
                params: { limit: 1000 },
            });
            commit("updateStocks", response.data.results);
            commit("updateStocksCount", response.data.count);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updateStocksLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateStocksLoading", false);
        }
    },

    async fetchBillingAccount({ commit, state, rootGetters }, params) {
        const permissions = rootGetters["session/current_user_permissions"];
        if (!canFetchOrderBillingAccount(state, permissions)) {
            commit("updateBillingAccount", null);
            return;
        }
        try {
            const response = await axios.get(
                `/api/billingaccounts/${state.order.buyer_data.billingaccount}/`
            );
            commit("updateBillingAccount", response.data);
            return response;
        } catch (xhr_error) {
            commit("updateBillingAccount", null);
            let error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async assignOrder({ commit, dispatch, state }, params) {

        const url = `/api/customerorders/${state.order.id}/assign/`;
        commit("updateAssigningOrder", true);

        try {
            const response = await axios.put(url, { user: params.user });
            dispatch("fetchOrder");
            commit("updateAssigningOrder", false);
            dispatch("fetchEvents");
            dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async billOrder({ commit, dispatch, state }, params) {

        const url = `/api/customerorders/${state.order.id}/bill/`;
        commit("updateOrderBilling", true);

        try {
            const response = await axios.post(url, { force: params.force });
            await dispatch("fetchOrder", { force: true });
            await dispatch("invoices/fetchInvoices", { force: true });
            dispatch("session/fetchStats", null, { root: true });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateOrderBilling", false);
        }
    },

    async createSubOrders({ commit, dispatch, state }, params) {

        const url = `/api/customerorders/${state.order.id}/createsuborders/`;
        commit("updateCreatingSuborders", true);

        try {
            const response = await axios.post(url);
            commit("updateCreatingSuborders", false);
            dispatch('update');
            return response;
        } catch (xhr_error) {
            commit("updateCreatingSuborders", false);
            throw utils.handleError(xhr_error);
        }
    },

    fetchCustomerStats({ commit, state }, params) {
        if (!state.order) {
            return;
        }
        const buyerId = catalogCustomerIdForOrder(state.order, store.getters["session/current_entity_id"]);
        if (!buyerId || !canReadCatalogCustomerForOrder(
            state.order, store.getters["session/current_entity_id"]
        )) {
            resetCustomerStatsFetch();
            commit("updateCustomerStats", {});
            return;
        }
        if (params?.force !== true && sameOrderId(customerStatsBuyerId, buyerId)) {
            return inflightFetchCustomerStats;
        }
        commit("updateCustomerStatsLoading", true);
        customerStatsBuyerId = buyerId;
        inflightFetchCustomerStats = requestCustomerStats(commit, buyerId);
        return inflightFetchCustomerStats;
    },

    async init({ commit, dispatch }, { order }) {
        resetInflightFetchOrderItems();
        resetInflightFetchOrder();
        await commit("updateBillingAccount", null);

        // Init nested list URLs before exposing the order: child blocks mount as
        // soon as `order` is set and would otherwise fetch the default endpoints.
        await dispatch("documents/init", {
            tags: `order:${order.order_id}`,
            create_tags: [`order:${order.order_id}`],
        });
        await dispatch("catalogentries/init", {
            url: `/api/customerorders/${order.id}/catalogentries/`,
            storageKey: getStorageKey('customerorder', 'catalogentries'),
            fieldsToSave: ['limit', 'ordering'],
            fetch_stats: false,
            filters: {
                ordering: "label",
                type: 'product'
            },
        });
        await dispatch("catalogs/init", {
            url: `/api/customerorders/${order.id}/catalogs/`,
            storageKey: getStorageKey('customerorder', 'catalogs'),
            fieldsToSave: ['limit', 'ordering'],
            fetch_stats: false,
            filters: {
                ordering: "name",
            },
        });
        await dispatch("invoices/init", {
            url: `/api/customerorders/${order.id}/invoices/`,
            storageKey: getStorageKey('customerorder', 'invoices'),
            fieldsToSave: ['limit', 'ordering'],
            filters: { order: order.id, include_suborders: true },
        });

        await dispatch("suborders/init", {
            url: `/api/customerorders/${order.id}/suborders/`,
            storageKey: getStorageKey('customerorder', 'suborders'),
            fieldsToSave: ['limit', 'ordering'],
            fetch_stats: false,
        });

        await commit("updateOrder", order);
    },

    update({ commit, dispatch, state }, params) {
        const opts = params || {};
        const section = opts.section || null;
        const fullRefresh = !section;

        if (opts.fetchOrder === true || (fullRefresh && opts.fetchOrder !== false)) {
            dispatch("fetchOrder");
        }

        if (fullRefresh || shouldFetchSection(section, "billing")) {
            dispatch("fetchBillingAccount");
        }

        if (shouldFetchSection(section, "items")) {
            dispatch("fetchOrderItems");
        }

        // Items/logistics blocks fetch suborders on mount. Billing has no such
        // block, but InvoiceFlowChecklist needs the list — so load them on
        // billing (and on a full refresh). Do not fetch on items/logistics
        // section switches: that would duplicate the block request and get
        // canceled by the suborders cancel token.
        if (
            (state.order?.suborders_needed || state.order?.suborders_count) &&
            (fullRefresh || shouldFetchSection(section, "billing"))
        ) {
            dispatch("suborders/fetchOrders");
        }

        if (state.order?.ready === false) {
            dispatch("catalogs/fetchCatalogs");
            if (shouldFetchSection(section, "items")) {
                dispatch("catalogentries/fetchCatalogEntries");
            }
        } else {
            if (shouldFetchSection(section, "logistics")) {
                dispatch("fetchShipments");
            }
            if (shouldFetchSection(section, "billing")) {
                dispatch("invoices/fetchInvoices");
            }
        }

        if (
            (fullRefresh || opts.fetchEvents === true) &&
            state.order?.ready !== false
        ) {
            dispatch("fetchEvents");
        }

        if (fullRefresh || shouldFetchSection(section, "documents")) {
            dispatch("documents/fetchDocuments");
        }

        if (fullRefresh) {
            dispatch("session/fetchStats", null, { root: true });
        }
    },

    async duplicateOrder({ commit, dispatch, state }, params) {
        try {
            const url = `/api/customerorders/${state.order.id}/duplicate/`;
            const response = await axios.post(url);
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            return Promise.reject(utils.handleError(xhr_error));
        }
    },

    downloadOrderForm({ commit, dispatch, state }, params) {
        const url = `/api/customerorders/${state.order.id}/form/`;
        window.open(url, "_blank");
    },

    async markCustomerOrderFormCommunicatedAction({ commit, dispatch, state }) {
        const url = `/api/customerorders/${state.order.id}/sendformtocustomer/`;
        commit("updateMarkingCustomerOrderFormCommunicated", true);
        try {
            const response = await axios.post(url, { send_email: false });
            await dispatch("fetchOrder");
            await dispatch("fetchEvents");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateMarkingCustomerOrderFormCommunicated", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        suborders: orders({
            storageKey: getStorageKey('customerorder', 'suborders'),
            fieldsToSave: ['limit', 'ordering']
        }),
        catalogs: catalogs({
            storageKey: getStorageKey('customerorder', 'catalogs'),
            fieldsToSave: ['limit', 'ordering']
        }),
        documents: documents({
            storageKey: getStorageKey('customerorder', 'documents'),
            fieldsToSave: ['limit', 'ordering']
        }),
        customerstats: customerstats(),
        catalogentries: catalogentries({
            storageKey: getStorageKey('customerorder', 'catalogentries'),
            fieldsToSave: ['limit', 'ordering']
        }),
        invoices: invoices({
            storageKey: getStorageKey('customerorder', 'invoices'),
            fieldsToSave: ['limit', 'ordering']
        }),
    },
};
